import { createElement, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { UserMenuItem } from '@swo/menu-common';
import { LinkComponent, commonNavigation } from 'swo-ciam-common-react';
import { paramCase } from 'param-case';
import { useAbility } from '@casl/react';
import { useTranslation } from 'react-i18next';
import { LocaleTranslationKeysEnum } from '@src/core/i18n/locale-translation-keys.enum';
import {
  ApplicationConfigurationKeyEnum,
  ApplicationConfigurationService,
} from '@src/core/application-configuration';
import { RBACProtectionContext } from '@src/core/rbac/rbac-protection.provider';
import { CMPPermissions, CMPResourceGroup } from '@src/core/rbac/types/rbac-restriction.enum';
import { updateRecentlyVisited } from '@src/domain/common/utils/quick-links/quick-links';
import { RoutePathsEnum } from '../route-paths.enum';

type ExtendedUserMenuItem = UserMenuItem & { linkType: string; id: string };

interface ProfileLinksInterface {
  links: ExtendedUserMenuItem[];
  itemInsertionWorkaround: (event: MouseEvent) => void;
}

// Random enough but stable, non-standard, but similar UUIDs for custom handling purposes
const contactUsId = '-a3d7c2e1-4b89-4f3d-9a12-7c8f5e2b9d34';
const languageSettingsId = '-f8b1e4c0-9d23-4a76-8c45-2e7a1b3f6d90';
const notificationSettingsId = '-4d8e1b0-b674-4786-8f60-91104b505c62';
const userProfileId = '-c4e9a7b2-3f56-4d8e-9b01-6a2d8f4c7e53';
const userManagementId = '-e7f3d1a9-5c42-4b6f-8d29-9f0a3e7b2c61';
const privacySettingsId = '-b2d8f4e7-6a13-4c59-9e84-3f7d2a1b5c90';

const workaroundCustomLinksInsideSWOProfile: ExtendedUserMenuItem[] = [
  {
    id: contactUsId,
    name: LocaleTranslationKeysEnum.SwoUserInfoMenuCommonContactUs,
    url: ApplicationConfigurationService.getValue(
      ApplicationConfigurationKeyEnum.contactUs
    ) as string,
    linkType: 'external',
  },
  {
    id: languageSettingsId,
    name: LocaleTranslationKeysEnum.SwoUserInfoMenuUserLanguageSettings,
    url: RoutePathsEnum.Settings,
    linkType: 'internalRoute',
  },
  {
    id: notificationSettingsId,
    name: LocaleTranslationKeysEnum.SwoUserInfoMenuUserNotificationSettings,
    url: RoutePathsEnum.NotificationSettings,
    linkType: 'internalRoute',
  },
  {
    id: userProfileId,
    name: LocaleTranslationKeysEnum.SwoUserInfoMenuUserProfileSettings,
    url: ApplicationConfigurationService.getValue(
      ApplicationConfigurationKeyEnum.profileSettings
    ) as string,
    linkType: 'internalApp',
  },
  {
    id: userManagementId,
    name: LocaleTranslationKeysEnum.SwoUserInfoMenuUserManagement,
    url: ApplicationConfigurationService.getValue(
      ApplicationConfigurationKeyEnum.userSettings
    ) as string,
    linkType: 'internalApp',
  },
  {
    id: privacySettingsId,
    name: LocaleTranslationKeysEnum.SwoUserInfoMenuPrivacySettings,
    url: '/privacy',
    linkType: 'custom',
  },
];

const insertLink = (
  placeholderNode: Node,
  linkId: string,
  name: string,
  url: string,
  linkType: string,
  onClick?: (event: MouseEvent) => void
) => {
  const possibleIndicators = {
    external: 'launch',
    internalRoute: null,
    internalApp: null,
    currentPage: 'check',
  };

  const isExternal = linkType === 'external';

  const linkComponentElement = createElement(LinkComponent, {
    id: linkId,
    testId: `profile-link-${paramCase(name)}`,
    url,
    displayName: name,
    indicator:
      possibleIndicators[window.location.href.includes(url) ? 'currentPage' : 'none'] ??
      possibleIndicators[linkType],
    navigateMethod: () => commonNavigation(url, linkType),
    hoverable: true,
    clean: !isExternal,
    danger: false,
    target: isExternal ? '_blank' : '_self',
    rel: isExternal ? 'noopener noreferrer' : undefined,
    onClickOutsideHandler: () => {
      updateRecentlyVisited(linkId);
    },
    onClick,
  });

  const documentFragment = document.createDocumentFragment();
  ReactDOM.render(linkComponentElement, documentFragment);

  const placeholderNodeAnchor = placeholderNode.parentElement.querySelector(
    `a[aria-label='${name}']`
  );

  placeholderNodeAnchor.setAttribute('href', url);
  placeholderNodeAnchor.setAttribute(
    'style',
    'display: flex; width: auto; justify-content: space-between;'
  );

  if (linkType !== 'internalRoute') {
    placeholderNodeAnchor.addEventListener('click', (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (linkType !== 'custom') {
        updateRecentlyVisited(linkId);
      }
      commonNavigation(url, linkType);

      if (onClick) {
        onClick(event);
      }
    });
  }

  if (linkType === 'internalRoute') {
    placeholderNodeAnchor.addEventListener('click', () => {
      updateRecentlyVisited(linkId);
    });
  }

  placeholderNodeAnchor.innerHTML = documentFragment.firstElementChild.innerHTML;
};

export const useProfileLinks = (): ProfileLinksInterface => {
  const ability = useAbility(RBACProtectionContext);

  const canViewCiam = ability.can(CMPPermissions.FullAccess, CMPResourceGroup.CIAM);
  const canViewPublisherUpdates = ability.can(
    CMPPermissions.FullAccess,
    CMPResourceGroup.PublisherUpdates
  );

  const { t: translateText } = useTranslation();

  const itemInsertionWorkaround = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      const target = event.target as HTMLElement;

      setTimeout(() => {
        const menu = target.querySelector('menu');
        if (!menu) {
          itemInsertionWorkaround(event);
          return;
        }

        const access = {
          [notificationSettingsId]: canViewPublisherUpdates,
          [userManagementId]: canViewCiam,
        };

        const customHandler = {
          [privacySettingsId]: (clickEvent: MouseEvent) => {
            clickEvent.preventDefault();
            clickEvent.stopPropagation();
            globalThis?.UC_UI?.showSecondLayer();
          },
        };

        workaroundCustomLinksInsideSWOProfile.forEach((item) => {
          if (Reflect.has(access, item.id) && !access[item.id]) {
            return;
          }

          insertLink(
            menu,
            item.id,
            translateText(item.name),
            item.url,
            item.linkType,
            customHandler[item.id]
          );
        });
      }, 0);
    },
    [canViewCiam, canViewPublisherUpdates, translateText]
  );

  const profileMenuLinks = useMemo(
    () =>
      workaroundCustomLinksInsideSWOProfile
        .filter((link) => {
          if (link.name === LocaleTranslationKeysEnum.SwoUserInfoMenuUserManagement) {
            return canViewCiam;
          }
          if (link.name === LocaleTranslationKeysEnum.SwoUserInfoMenuUserNotificationSettings) {
            return canViewPublisherUpdates;
          }
          return true;
        })
        .map((item) => ({
          id: item.id,
          name: translateText(item.name),
          url: item.url,
          linkType: item.linkType,
        })),
    [canViewCiam, canViewPublisherUpdates, translateText]
  );

  return { links: profileMenuLinks, itemInsertionWorkaround };
};
